import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { MapImportDto, Page } from "@tcr/contracts";
import { api, isApiError, toItems } from "./client";
import { EP } from "./endpoints";
import { qk } from "./keys";
import { friendlyMessage } from "./errors";
import { upsertList } from "./robots";

const active = (imp: MapImportDto | undefined) => !!imp && (imp.status === "pending" || imp.status === "running");

/** Recent map imports for a robot, newest first. Hidden (empty) when the backend lacks the route. */
export function useMapImports(robotId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.robotMapImports(robotId ?? ""),
    queryFn: async () => {
      try {
        return toItems(await api.get<MapImportDto[] | Page<MapImportDto>>(EP.robotMapImports(robotId!)));
      } catch (e) {
        if (isApiError(e, "NotFound")) return [];
        throw e;
      }
    },
    enabled: !!robotId && enabled,
  });
}

/**
 * One import. Realtime `map_import.updated` keeps it current; while it is still in
 * progress it is also polled as a fallback in case the event is missed. A polled result
 * goes through `mergeMapImport` like the event would, so the list and maps catch up too.
 */
export function useMapImport(id: string | null | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.mapImport(id ?? ""),
    queryFn: async () => {
      const imp = await api.get<MapImportDto>(EP.mapImport(id!));
      mergeMapImport(qc, imp);
      return imp;
    },
    enabled: !!id,
    refetchInterval: (q) => (active(q.state.data) ? 3000 : false),
  });
}

/** Write an import into every cache holding it; refresh maps once it has produced one. */
export function mergeMapImport(qc: QueryClient, imp: MapImportDto): void {
  const prev = qc.getQueryData<MapImportDto>(qk.mapImport(imp.id));
  qc.setQueryData<MapImportDto>(qk.mapImport(imp.id), (old) => (old ? { ...old, ...imp } : imp));
  upsertList(qc, qk.robotMapImports(imp.robotId), imp, true);
  if (imp.status === "succeeded" && prev?.status !== "succeeded") {
    void qc.invalidateQueries({ queryKey: qk.maps });
    if (imp.mapId) void qc.invalidateQueries({ queryKey: qk.map(imp.mapId) });
  }
}

/** Plain-language reason a start request was refused. */
export function startMapImportError(e: unknown): string {
  if (isApiError(e, "Conflict")) {
    return /disabled/i.test(e.message)
      ? "This robot is disabled."
      : "A map with this name already exists in this site. Choose another name, or add the import as a new version of that map.";
  }
  if (isApiError(e, "CapabilityMissing")) return "This robot does not support exporting its map (it does not report map.export.v1).";
  return friendlyMessage(e);
}

export function useStartMapImport(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; mapId?: string }) => api.post<MapImportDto>(EP.robotMapImports(robotId), body),
    onSuccess: (imp) => mergeMapImport(qc, imp),
    // Errors are shown inline in the import dialog (startMapImportError).
  });
}
